/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { AxiosError, AxiosHeaders, AxiosResponse, Method } from "axios";
import { useState, useCallback } from "react";
import axios from "../http/axios";
import store, { authAction, eventAction, useAppDispatch } from "../store/store";
import { useTranslation } from "react-i18next";
import { GenericHTTPResponseInterface } from "@malmur-technologies/medicalsystem-shared";

/**
 * Type definition for HTTP error responses from the core backend.
 * Contains status code, error message, detailed error information, and request path.
 */
type HTTPCoreErrorResponse = {
  statusCode: number;
  timestamp: Date;
  message: string;
  detail: {
    statusCode: number;
    message: string;
    error: string;
  };
  path: string;
};

/**
 * Type definition for HTTP headers, supports both array and record formats.
 */
type Headers = { header: string; value: string }[] | Record<string, string>;

/**
 * Custom React hook for making authenticated HTTP requests with error handling.
 * Manages loading state, error messages, and automatic token refresh/logout on auth errors.
 * Supports GET, POST, PUT, DELETE with FormData and JSON payloads.
 * @returns Tuple with [isLoading, error, sendRequest] where sendRequest is the HTTP request function
 */
const useHttp = () => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();
  const getHeaders = (headers: Headers | null | undefined): Record<string, string> => {
    if (!headers) return {};

    if (!Array.isArray(headers)) {
      return headers as Record<string, string>;
    }
    let headerObject: Record<string, string> = {};
    for (let header of headers) {
      headerObject[header.header] = header.value;
    }
    return headerObject;
  };

  const getResponse = async (
    httpMethod: Method,
    endPointPath: string,
    headers?: Record<string, string>,
    httpBody?: string | FormData | null
  ) => {
    const isFormData = httpBody instanceof FormData;
    if (isFormData && headers && "Content-Type" in headers) {
      delete headers["Content-Type"];
    }
    const config = {
      headers: headers,
      timeout: isFormData ? Number(process.env.REACT_APP_TIMEOUT) * 3 : Number(process.env.REACT_APP_TIMEOUT),
      transformRequest: isFormData ? [(data) => data] : undefined,
    };
    switch (httpMethod.toUpperCase()) {
      case "GET":
        return await axios.get(endPointPath, config);
      case "POST":
        return await axios.post(endPointPath, httpBody, config);
      case "PUT":
        return await axios.put(endPointPath, httpBody, config);
      case "DELETE":
        return await axios.delete(endPointPath, config);
      default:
        throw Error("Not implemented http Request type");
    }
  };

  function getErrorMessage(
    httpCoreError: HTTPCoreErrorResponse,
    message: string,
    code: number,
    axiosError: AxiosError<unknown, any>
  ) {
    const responseData = axiosError.response?.data as any;

    const tryParseAndTranslateError = (msg: string): string => {
      if (!msg) return msg;
      try {
        const parsed = JSON.parse(msg);
        if (parsed.key && typeof parsed.key === 'string') {
          const translationKey = `errors.${parsed.key}`;
          const translated = t(translationKey, { ...parsed.params, defaultValue: msg }) as string;
          return translated !== translationKey ? translated : msg;
        }
      } catch (e) {
      }
      return msg;
    };

    if (responseData && typeof responseData === 'object' && 'success' in responseData) {
      const wrappedError = responseData as GenericHTTPResponseInterface<any>;
      if (!wrappedError.success) {
        const translatedMessage = tryParseAndTranslateError(wrappedError.message || "");
        return {
          message: translatedMessage || t("coreErrorMessage.unknownError"),
          code: wrappedError.status || axiosError.response?.status || 500
        };
      }
    }
    if (httpCoreError?.detail) {
      code = httpCoreError.statusCode;
      if (Array.isArray(httpCoreError.detail.message) && httpCoreError.detail.message.length > 0) {
        const validationMessages = httpCoreError.detail.message.join(', ');
        message = `ValidationFailed: ${validationMessages}`;
      } else if (typeof httpCoreError.detail.message === 'string') {
        message = tryParseAndTranslateError(httpCoreError.detail.message);
      } else {
        message = httpCoreError.detail.error || t("coreErrorMessage.validationError");
      }
    } else if (axiosError?.isAxiosError) {
      code = axiosError.response?.status || 500;
      if (responseData?.detail && Array.isArray(responseData.detail.message)) {
        const validationMessages = responseData.detail.message.join(', ');
        message = `Bad Request: ${validationMessages}`;
      } else if (responseData?.detail?.message) {
        message = tryParseAndTranslateError(responseData.detail.message);
      } else if (axiosError.code === AxiosError.ERR_BAD_REQUEST && axiosError.response?.status === 403 && !(axiosError.config?.url === "/user/auth/login")) {
        message = t("coreErrorMessage.forbiddenError");
      } else if (axiosError.response?.status === 401) {
        message = t("coreErrorMessage.unauthorizedError");
      } else if (axiosError.response?.status === 400) {
        if (responseData?.message === "Bad Request" && responseData?.detail) {
          if (Array.isArray(responseData.detail.message)) {
            const validationMessages = responseData.detail.message.join(', ');
            message = `Bad Request: ${validationMessages}`;
          } else {
            message = tryParseAndTranslateError(responseData.detail.message || "") || t("coreErrorMessage.validationError");
          }
        } else {
          const backendMessage = responseData?.message || t("coreErrorMessage.badRequestError");
          message = tryParseAndTranslateError(backendMessage);
          const translatedMessage = t(`progressNoteForm.${message}`, { defaultValue: message });
          message = translatedMessage !== `progressNoteForm.${message}` ? translatedMessage : message;
        }
      } else if (axiosError.response?.status === 500) {
        if (axiosError.config?.url === "/user/auth/login") {
          message = t("coreErrorMessage.payloadLoginError");
        } else {
          message = t("coreErrorMessage.payloadError");
        }
      } else if (axiosError.response?.status === 404) {
        if (axiosError.config?.url === "/accounting") {
          message = t("coreErrorMessage.accountNotFoundError");
        } else {
          message = tryParseAndTranslateError(responseData?.message || "") || responseData?.message;
        }
      } else if (axiosError.code === AxiosError.ERR_NETWORK) {
        message = t("coreErrorMessage.networkError");
      } else if (axiosError.code === "ECONNABORTED") {
        message = t("coreErrorMessage.timeoutError", "Request timeout");
      } else {
        if (responseData?.message) {
          message = tryParseAndTranslateError(responseData.message);
        } else {
          message = axiosError.message || t("coreErrorMessage.unknownError");
        }
      }
    }
    return { message, code };
  }
  const sendRequest = useCallback(
    async <R = {}, P = {}>(
      endPointPath: string,
      applyDataFn: (data: R, payload?: P) => void,
      httpMethod: Method,
      httpBody?: string | FormData | null,
      httpHeaders?: Headers | null,
      params?: {
        applyErrorFn?: (message: string, payload?: P) => void;
        indexRequest?: number;
        totalRequests?: number;
        payload?: P;
        successMessage?: string;
      }
    ) => {
      setIsLoading(true);
      setError(null);

      const method = httpMethod.toUpperCase();
      if (method === 'POST' || method === 'PUT' || method === 'DELETE') {
        dispatch(eventAction.setGlobalLoading(true));
      }

      window.dispatchEvent(new CustomEvent('http-loading-start', {
        detail: { url: endPointPath }
      }));

      let response: AxiosResponse<any, any>;
      const headers = getHeaders(httpHeaders);
      try {
        response = await getResponse(httpMethod, endPointPath, headers, httpBody);

        if (response !== null) {
          if (response.status >= 200 && response.status < 300) {
            if (applyDataFn) applyDataFn(response.data, params?.payload);
            const method = httpMethod.toUpperCase();
            if (method === 'POST' || method === 'PUT' || method === 'DELETE') {
              dispatch(eventAction.setGlobalLoading(false));
            }
            if (method !== "GET") {
              if (params?.successMessage) {
                dispatch(
                  eventAction.addSuccessMessage({
                    message: params.successMessage,
                    code: response.status.toString(),
                    id: crypto.randomUUID()
                  })
                );
              } else if (response.statusText !== "OK") {
                dispatch(
                  eventAction.addSuccessMessage({
                    message: response.statusText,
                    code: response.status.toString(),
                    id: crypto.randomUUID()
                  })
                );
              }
            }
          } else {
            if (params?.applyErrorFn) {
              params.applyErrorFn(response.statusText, params?.payload);
            }
            setError(response.statusText);
            dispatch(
              eventAction.addErrorMessage({
                message: response.statusText,
                code: response.status.toString(),
                id: crypto.randomUUID()
              })
            );
          }
        }
      } catch (error: any) {
        if (httpBody instanceof FormData) {
          console.error("FormData upload error details:", {
            message: error.message,
            code: error.code,
            status: error.response?.status,
            statusText: error.response?.statusText,
            responseData: error.response?.data,
            config: {
              url: error.config?.url,
              method: error.config?.method,
              headers: error.config?.headers,
              timeout: error.config?.timeout,
            },
          });

          if (error.config) {
            console.error("Failed request config:", {
              url: error.config.url,
              method: error.config.method,
              baseURL: error.config.baseURL,
              headers: error.config.headers,
            });
          }
        }
        const httpCoreError = error as HTTPCoreErrorResponse;
        const axiosError = error as AxiosError;
        let code = 500;
        let message = "Unknown error";
        ({ message, code } = getErrorMessage(httpCoreError, message, code, axiosError));

        if (params?.applyErrorFn) {
          params.applyErrorFn(message, params?.payload);
        }
        const method = httpMethod.toUpperCase();
        if (method === 'POST' || method === 'PUT' || method === 'DELETE') {
          dispatch(eventAction.setGlobalLoading(false));
        }
        dispatch(eventAction.addErrorMessage({ message, code: code.toString(), id: crypto.randomUUID() }));
        setError(message);
        if (httpCoreError.statusCode === 403 || httpCoreError.statusCode === 401 || axiosError.response?.status === 401) {
          store.dispatch(authAction.logout());
          window.location.replace("/login");
        }
        response = null;
      }

      if (
        !params?.indexRequest ||
        !params?.totalRequests ||
        params.indexRequest === params.totalRequests
      ) {
        setIsLoading(false);
        window.dispatchEvent(new CustomEvent('http-loading-end'));
      }
      return response;
    },
    [dispatch, t]
  );
  return [isLoading, error, sendRequest] as const;
};
export default useHttp;

